import { Controller } from "@hotwired/stimulus"

// Prévisualisation registre (INSEE/ORIAS) + coches → champ caché JSON { field_selection: [...] } pour le save Company.
// Le fetch registre ne doit partir que lorsque le SIRET change (input/paste sur ce champ), pas sur les autres champs
// ni les coches — sinon le Turbo stream réécrit le cadre et recoche tout.
export default class extends Controller {
  static targets = ["payloadInput"]
  static values = {
    previewUrl: String,
  }

  connect() {
    this.timeout = null
    this.abortController = null
    this.registrySelection = {}
    this.previewSiretForSelection = ""
    this._siretEl = null

    this.boundSiretInput = () => this.scheduleFetch(false)
    this.boundSiretPaste = () => requestAnimationFrame(() => this.scheduleFetch(false))
    this.onDelegatedChange = this.onDelegatedChange.bind(this)
    this.onFormSubmitCapture = this.onFormSubmitCapture.bind(this)

    this.bindSiretListeners()
    this.element.addEventListener("change", this.onDelegatedChange, true)

    const form = this.element.closest("form")
    if (form) form.addEventListener("submit", this.onFormSubmitCapture, true)

    this.scheduleFetch(true)
  }

  disconnect() {
    this.unbindSiretListeners()
    this.element.removeEventListener("change", this.onDelegatedChange, true)

    const form = this.element.closest("form")
    if (form) form.removeEventListener("submit", this.onFormSubmitCapture, true)

    clearTimeout(this.timeout)
    this.abortController?.abort()
  }

  bindSiretListeners() {
    this.unbindSiretListeners()
    const el = this.siretInput()
    if (!el) return

    this._siretEl = el
    el.addEventListener("input", this.boundSiretInput)
    el.addEventListener("paste", this.boundSiretPaste)
  }

  unbindSiretListeners() {
    if (!this._siretEl) return

    this._siretEl.removeEventListener("input", this.boundSiretInput)
    this._siretEl.removeEventListener("paste", this.boundSiretPaste)
    this._siretEl = null
  }

  onDelegatedChange(event) {
    const cb = event.target
    if (!cb?.matches?.('input.registry-field-selection[type="checkbox"]')) return
    this.registrySelection[cb.value] = cb.checked
    this.updateHiddenPayload()
  }

  onFormSubmitCapture() {
    this.updateHiddenPayload()
  }

  scheduleFetch(immediate) {
    clearTimeout(this.timeout)
    const delay = immediate ? 0 : 550
    this.timeout = setTimeout(() => this.fetchPreview(), delay)
  }

  siretInput() {
    return this.element.querySelector(
      'input[name="company[siret]"], input[name="siret"], input[id*="siret"], input[name*="[siret]"]',
    )
  }

  normalizedSiret() {
    const input = this.siretInput()
    if (!input) return ""
    return input.value.replace(/\D/g, "")
  }

  async fetchPreview() {
    if (!this.hasPreviewUrlValue || !this.previewUrlValue) return

    const digits = this.normalizedSiret()
    const frame = document.getElementById("company_registry_preview")

    if (digits.length !== 14) {
      this.abortController?.abort()
      if (frame) frame.innerHTML = ""
      this.previewSiretForSelection = ""
      this.registrySelection = {}
      if (this.hasPayloadInputTarget) this.payloadInputTarget.value = ""
      return
    }

    clearTimeout(this.timeout)
    this.abortController?.abort()
    this.abortController = new AbortController()

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const body = new URLSearchParams({ siret: digits })
    if (token) body.append("authenticity_token", token)

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": token,
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
        body,
        signal: this.abortController.signal,
      })

      if (!response.ok) return

      const html = await response.text()
      if (typeof Turbo !== "undefined" && Turbo.renderStreamMessage) {
        Turbo.renderStreamMessage(html)
        requestAnimationFrame(() => this.afterPreviewRendered())
      }
    } catch (e) {
      if (e.name === "AbortError") return
      console.warn("[company-registry-preview]", e)
    }
  }

  afterPreviewRendered() {
    const digits = this.normalizedSiret()
    if (digits !== this.previewSiretForSelection) {
      this.registrySelection = {}
      this.previewSiretForSelection = digits
    }

    const frame = document.getElementById("company_registry_preview")
    if (!frame) return

    frame.querySelectorAll('input.registry-field-selection[type="checkbox"]').forEach((cb) => {
      const key = cb.value
      if (this.registrySelection[key] === undefined) this.registrySelection[key] = true
      cb.checked = this.registrySelection[key]
    })

    this.fillMainFormFieldsFromRegistry()
    this.updateHiddenPayload()
  }

  fillMainFormFieldsFromRegistry() {
    const frame = document.getElementById("company_registry_preview")
    if (!frame) return

    const payloadEl = frame.querySelector('#company_registry_assignable_json[type="application/json"]')
    if (!payloadEl?.textContent?.trim()) return

    let payload
    try {
      payload = JSON.parse(payloadEl.textContent)
    } catch {
      return
    }

    const legalCb = frame.querySelector("#reg_legal_name")
    if (legalCb?.checked && payload.legal_name) {
      const legalInput = this.element.querySelector(
        'input[name="company[legal_name]"], textarea[name="company[legal_name]"]',
      )
      if (legalInput) {
        legalInput.value = payload.legal_name
        legalInput.dispatchEvent(new Event("input", { bubbles: true }))
        legalInput.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    const nameCb = legalCb
    if (nameCb?.checked && payload.trade_name) {
      const nameInput = this.element.querySelector('input[name="company[name]"], textarea[name="company[name]"]')
      if (nameInput && !String(nameInput.value || "").trim()) {
        nameInput.value = payload.trade_name
        nameInput.dispatchEvent(new Event("input", { bubbles: true }))
        nameInput.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    const apeCb = frame.querySelector("#reg_ape_code")
    if (apeCb?.checked && payload.ape_code) {
      const apeInput = this.element.querySelector('input[name="company[ape_code]"]')
      if (apeInput) {
        apeInput.value = payload.ape_code
        apeInput.dispatchEvent(new Event("input", { bubbles: true }))
        apeInput.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    const addressCb = frame.querySelector("#reg_address")
    if (addressCb?.checked && payload.address_attributes) {
      const addr = payload.address_attributes
      ;["line1", "line2", "zipcode", "city", "country"].forEach((field) => {
        const value = addr[field]
        if (!value) return

        const input = this.element.querySelector(
          `input[name*="[address_attributes][${field}]"], textarea[name*="[address_attributes][${field}]"]`,
        )
        if (!input) return

        input.value = value
        input.dispatchEvent(new Event("input", { bubbles: true }))
        input.dispatchEvent(new Event("change", { bubbles: true }))
      })
    }
  }

  updateHiddenPayload() {
    if (!this.hasPayloadInputTarget) return

    const digits = this.normalizedSiret()
    if (digits.length !== 14) {
      this.payloadInputTarget.value = ""
      return
    }

    const frame = document.getElementById("company_registry_preview")
    if (!frame) {
      this.payloadInputTarget.value = ""
      return
    }

    const selected = []
    frame.querySelectorAll('input.registry-field-selection[type="checkbox"]').forEach((cb) => {
      if (cb.checked) selected.push(cb.value)
    })

    if (selected.length === 0) {
      this.payloadInputTarget.value = ""
      return
    }

    this.payloadInputTarget.value = JSON.stringify({ field_selection: selected })
  }
}
